import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  dischargeFromDiscipline,
  rejectCaseload,
  approveCaseload,
  getCaseloadDetails, removeStudentFromCaseload, getCaseloadsByStudentDisciplineId, batchUpdateManyCaseloads, updateCaseload, changeStudentSchool
} from '../../api/services/caseloadService';
import { getDivisionPlacements } from '../../api/services/placementService';
import { getDivisionEsys } from '../../api/services/esyService';
import { getGenders } from '../../api/services/genderService';
import { Notification, ResponseStatus } from '../../components/notifications';
import { FieldInput } from '../../components/fieldInput';
import Layout from '../../components/layout';
import SubmitButton from '../../components/submitButton';
import LayoutError from '../../components/layoutError';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Modal from '../../components/modal';
import ReactToolTip from 'react-tooltip';
import 'react-datepicker/dist/react-datepicker.css';
import TreatmentPlan from '../../api/models/treatmentPlan';
import { getFrequencies } from '../../api/services/frequencyService';
import { getSchoolHistory, getStudentTreatments } from '../../api/services/studentService';
import EditModal from '../../components/editModal';
import { getCurrentUser } from '../../api/services/meService';
import User from '../../api/models/user';
import { Role, RoleIdLookup } from '../../common/constants';
import UserRole from '../../api/models/userrole';
import { Tab } from '@headlessui/react';
import Student from '../../api/models/student';
import { getTherapistRoles } from '../../api/services/therapistRolesService';
import { hasValue } from '../../common/utils';
import { getGrades } from '../../api/services/gradeService';
import District from '../../api/models/district';
import School from '../../api/models/school';
import { getDistricts, getTherapistDistricts, getTherapistDistrictsByCaseloadId } from '../../api/services/districtService';
import DistrictsResponse from '../../api/responses/districtsResponse';
import Caseload from '../../api/models/caseload';
import { getSchoolsByDistrict } from '../../api/services/schoolService';
import CaseloadUpdateRequest from '../../api/requests/caseloadUpdateRequest';
import { getStudentStatuses } from '../../api/services/studentStatusService';
import EditStudentTabPanel from '../../components/EditStudentTabPanel';
import { useAppSelector } from '../../app/hooks';
import AdditionalCaseloadsResponse from '../../api/responses/additionalCaseloadsResponse';
import { selectOption } from '../../types/selectOption';
import CaseloadApprovalRequest from '../../api/requests/caseloadApprovalRequest';
import { IsAllowed } from '../../components/displayFor';
import StudentSchoolHistory from '../../api/models/studentSchoolHistory';
import StudentSchoolHistoryModal from '../../components/SchoolHistoryModal';
import State from '../../api/models/state';
import { getStates } from '../../api/services/stateService';
import { getHomeDistricts, getHomeDistrictsByStateId } from '../../api/services/homeDistrictService';
import HomeDistrict from '../../api/models/homeDistrict';
import Select, { SingleValue } from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';
import ChangeStudentSchoolRequest from '../../api/requests/changeStudentSchoolRequest';

export type onchange = {
  selections: any
}

type option = { label: string; value: number; };

type ViewState = {
  disciplineId: number;
  referrer: string;
  idSpecific?: string;
  therapistFirstName: string;
  therapistLastName: string;
};

type Params = {
  id: string;
};

const fetchPlacement = async (args: any[]) => {
  return await getDivisionPlacements(args[0]);
};

const fetchGenders = async () => {
  return await getGenders();
};

const fetchEsys = async (args: any[]) => {
  return await getDivisionEsys(args[0]);
};

const fetchFrequencies = async () => {
  return await getFrequencies();
};

const fetchStudentTreatments = async (studentId: number) => {
  return getStudentTreatments(studentId);
};

var startingHomeDistrict = 0;

const fetchStudentDetails = async ({ id }: any) => getCaseloadDetails(id);
const fetchTherapistRoles = async () => getTherapistRoles();
const fetchCaseloadsByStudentId = async (args: any[]) => getCaseloadsByStudentDisciplineId(args[0], args[1], args[2]);
const fetchStudentStatuses = async () => getStudentStatuses();
const fetchSchoolHistory = async (args: any[]) => getSchoolHistory(args[0]);
const fetchStates = async () => await getStates();
const fetchHomeDistricts = async () => await getHomeDistricts();
const fetchHomeDistrictsByStateId = async (args: any[]) => {
  return await getHomeDistrictsByStateId(args[0] ?? 0);
}

const changeSchoolFunc = async (args: any[]) => {
  let model: ChangeStudentSchoolRequest = {
    studentId: args[0],
    schoolId: args[1],
    startDate: args[2]
  };

  return await changeStudentSchool(model);
}

const EditStudent = () => {
  const history = useHistory();
  const blankReq: CaseloadUpdateRequest = {};
  const { state } = useLocation<ViewState>();
  const { id } = useParams<Params>();
  const [disciplineId, setDisciplineId] = useState<number>();
  const [caseloadId, setCaseloadId] = useState<number | undefined>(Number(id));
  const [clientStudentId, setClientStudentId] = useState('');
  const [studentFirstName, setStudentFirstName] = useState('');
  const [studentMi, setStudentMi] = useState('');
  const [studentLastName, setStudentLastName] = useState('');
  const [studentSuffix, setStudentSuffix] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [districtName, setDistrictName] = useState('');
  const [treatment, setTreatment] = useState<number | undefined>(0);
  const [placement, setPlacement] = useState<number | undefined>(0);
  const [maEligible, setMaEligible] = useState(false);
  const [studentId, setStudentId] = useState<number>(0);
  const [iepEndDate, setIepEndDate] = useState<Date | undefined>();
  const [gender, setGender] = useState<number | undefined>(0);
  const [esy, setEsy] = useState<number | undefined>();
  const [caseloadStartDate, setCaseloadStartDate] = useState<Date>();
  const [caseloadEndDate, setCaseloadEndDate] = useState<Date>();
  const [disciplineEndDate, setDisciplineEndDate] = useState<Date>();
  const [openEndDateWarning, setOpenEndDateWarning] = useState(false);
  const [openDischargeWarning, setOpenDischargeWarning] = useState(false);
  const [openRemoveWarning, setOpenRemoveWarning] = useState(false);
  const [treatmentPlans] = useState<TreatmentPlan[]>([]);
  const [iepStartDate, setIepStartDate] = useState<Date | undefined>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [studentTreatmentsLength, setStudentTreatmentsLength] = useState<number>();
  const [treatmentsAssigned, setTreatmentsAssigned] = useState<selectOption[]>([]);
  const [studentStatus, setStudentStatus] = useState<string>('');
  const [caseloads, setCaseloads] = useState<Caseload[]>([]);
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);

  //User hooks
  const [currentUser, setCurrentUser] = useState<User>();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [canSeeDOB, setCanSeeDOB] = useState<boolean>(false);
  const [isTherapist, setIsTherapist] = useState<boolean>(false);
  const [isBillingSpecialist, setIsBillingSpecialist] = useState<boolean>(false);
  const [isSuperUser, setIsSuperUser] = useState<boolean>(false);
  const [student, setStudent] = useState<Student>();
  const [caseload, setCaseload] = useState<Caseload>();

  const [openRejectConfirmModal, setOpenRejectConfirmModal] = useState<boolean>(false);
  const [studentDob, setStudentDob] = useState<Date>(new Date());
  const [grade, setGrade] = useState<number>(0);
  const [districtsList, setDistrictsList] = useState<District[]>([]);
  const [districtId, setDistrictId] = useState<number>(0);
  const [district, setDistrict] = useState<District>();
  const [isDistrictEsy, setIsDistrictEsy] = useState<boolean>(false);
  const [schools, setSchools] = useState<School[]>([]);
  const [schoolId, setSchoolId] = useState<number>(0);
  const [modalSchoolStartDate, setModalSchoolStartDate] = useState<Date>();
  const [changeDistrictId, setChangeDistrictId] = useState<number>(0);
  const [changeModalSchoolId, setChangeModalSchoolId] = useState<number>(0);
  const [ChangeErrorMessage, setChangeErrorMessage] = useState<string>('')

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [tabContent, setTabContent] = useState<CaseloadUpdateRequest>(blankReq);
  const [tabContentTrigger, setTabContentTrigger] = useState<boolean>(false);

  const studentStatusesData = useAsync({ promiseFn: fetchStudentStatuses });
  const userRoles = useAppSelector((s) => s.user.roles);

  const [notes, setNotes] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [isRejected, setIsRejected] = useState<boolean>(false);

  const [schoolHistory, setSchoolHistory] = useState<StudentSchoolHistory[]>([]);
  const [showSchoolHistoryModal, setShowSchoolHistoryModal] = useState<boolean>(false);
  const [defaultDistrict, setDefaultDistrict] = useState<number>();
  const [isFetchingDistricts, setIsFetchingDistricts] = useState<boolean>(true);
  const [isFetchingSchools, setIsFetchingSchools] = useState<boolean>(false);

  const [states, setStates] = useState<State[]>();
  const [homeDistrictState, setHomeDistrictState] = useState<number>();
  const [homeDistricts, setHomeDistricts] = useState<option[]>();
  const [homeDistrict, setHomeDistrict] = useState<number | undefined>(0);
  const [homeDistrictOption, setHomeDistrictOption] = useState<option>();
  const [allHomeDistricts, setAllHomeDistricts] = useState<HomeDistrict[]>([]);
  const [changeSchoolModalOpen, setChangeSchoolModalOpen] = useState<boolean>(false);

  /**
   * Effect for when data is sent back from the EditStudentTabPanel component
   * This will save the data into the setCaseloads hook
   */
  useEffect(() => {
    setCaseloads(current => [...current.map(c => {
      if (c.caseloadId == tabContent.id) {
        c.caseloadEndDate = tabContent.caseloadEndDate as any;
        c.caseloadStartDate = tabContent.caseloadStartDate! as any;
        c.studentStatusId = tabContent?.studentStatusId;
        c.studentStatus = tabContent?.studentStatus ?? '';
        c.therapistRoleId = tabContent.therapistRoleId;
        c.esyId = tabContent.esyId!;
        c.treatmentId = tabContent.treatmentId;
        c.studentStatusId = tabContent.studentStatusId;
        c.therapistRoleId = tabContent.therapistRoleId;
        c.individual = tabContent.individual;
        c.individualFrequency = tabContent.individualFrequency;
        c.individualMinutes = tabContent.individualMinutes;
        c.group = tabContent.group;
        c.groupFrequency = tabContent.groupFrequency;
        c.groupMinutes = tabContent.groupMinutes;
        c.consult = tabContent.consult;
        c.consultFrequency = tabContent.consultFrequency;
        c.consultMinutes = tabContent.consultMinutes;
        c.treatmentId = tabContent.treatmentId;
        c.on504 = tabContent.on504;
        c.onIEP = tabContent.onIEP;
      }

      return c;
    })]);
  }, [tabContent]);

  /**
   * When the IEP Start Date is updated, make sure we update it across caseloads
   */
  useEffect(() => {
    setCaseloads(current => [...current.map(c => {
      c.iepStartDate = iepStartDate;
      return c;
    })])
  }, [iepStartDate]);

  /**
   * When the IEP End Date is updated, make sure we update it across caseloads
   */
  useEffect(() => {
    setCaseloads(current => [...current.map(c => {
      c.iepEndDate = iepEndDate;
      return c;
    })])
  }, [iepEndDate]);

  //This effect is used to process our state variable
  //Updated every time "state" var is modified
  useEffect(() => {
    if (state) {
      //First, check to see if there is a referrer
      state.referrer = state !== undefined && state.referrer && state.referrer !== undefined && state.referrer !== null ? state.referrer : '';

      //Otherwise, set the discipline id
      if (state && state !== null && state !== undefined)
        setDisciplineId(state.disciplineId && state.disciplineId !== null && state.disciplineId !== undefined ? state.disciplineId : -1);
    }
  }, [state]);

  const esyData = useAsync({ deferFn: fetchEsys });
  const placementData = useAsync({ deferFn: fetchPlacement });

  const fetchUser = async () => {
    const userResponse = await getCurrentUser();
    setCurrentUser(userResponse.data);
  }

  useEffect(() => {
    if (currentUser) {
      const roles: UserRole[] = currentUser.userRoles;

      const isBs = roles.some(x => x.roleId === RoleIdLookup.BillingSpecialist);
      const isSu = roles.some(x => x.roleId === RoleIdLookup.SuperUser);
      const isAd = roles.some(x => x.roleId === RoleIdLookup.Admin)
      setCanSeeDOB(roles.some(x => x.roleId === RoleIdLookup.Admin || x.roleId === RoleIdLookup.BillingSpecialist));
      setIsAdmin(isAd);
      setIsTherapist(roles.some(x => x.roleId === RoleIdLookup.Therapist));
      setIsBillingSpecialist(isBs);
      setIsSuperUser(isSu)

      if (districtsList.length === 0 && (isBs || isSu || isAd)) {
        getTherapistDistrictsByCaseloadId(Number(id))
          .then((districts: DistrictsResponse) => {
            const currentDistrict = districts.data.districts.find(x => x.districtId === districtId) ?? districts.data.districts[0];
            setDistrict(currentDistrict);
            setIsDistrictEsy((currentDistrict?.esyStartDate && moment(currentDistrict?.esyStartDate).isValid() && moment(currentDistrict?.esyStartDate).isSameOrBefore(moment())) ?? false);

            getSchoolsByDistrict(currentDistrict?.districtId ?? districtId).then(r => {
              // setSchools(r.data.schools);
              setIsFetchingDistricts(false);
            });
          });
        getDistricts().then((districts: DistrictsResponse) => setDistrictsList(districts.data.districts))
      }

      if (districtsList.length === 0 && !isBs && !isAd) {
        getTherapistDistricts(new Date().getMonth() + 1, new Date().getFullYear(), currentUser?.userId)
          .then((districts: DistrictsResponse) => {
            setDistrictsList(districts.data.districts);
            const currentDistrict = districts.data.districts.find(x => x.districtId === districtId) ?? districts.data.districts[0];
            setDistrict(currentDistrict);
            setIsDistrictEsy((currentDistrict?.esyStartDate && moment(currentDistrict?.esyStartDate).isValid() && moment(currentDistrict?.esyStartDate).isSameOrBefore(moment())) ?? false);

            getSchoolsByDistrict(currentDistrict?.districtId ?? districtId).then(r => {
              // setSchools(r.data.schools);
              setIsFetchingDistricts(false);
            });
          });
      }
    }
  }, [currentUser])

  //Whenever the value of districtId changes, we will refetch the schools
  useEffect(() => {
    const fetch = async (districtId: number) => {
      const request = await getSchoolsByDistrict(districtId);
      setIsFetchingSchools(false);
      // if (request?.data?.schools && request.data.schools.length > 0) {
      //   setSchools(request.data.schools);
      //   setSchoolId(request.data.schools[0].schoolId ?? schoolId);
      //   setModalSchoolId(request.data.schools[0].schoolId ?? schoolId);
      // }
    }

    if (districtId > 0 && districtsList.find(x => x.districtId === districtId) && !isFetchingDistricts) {
      //We want to run this asynchronously so we build an async function
      //and then call it here
      setIsFetchingSchools(true);
      fetch(districtId);

      const currentDistrict = districtsList.find(x => x.districtId === districtId) ?? districtsList[0];
      setDistrict(currentDistrict);
      setIsDistrictEsy((currentDistrict?.esyStartDate && moment(currentDistrict?.esyStartDate).isValid() && moment(currentDistrict?.esyStartDate).isSameOrBefore(moment())) ?? false);
    }
  }, [districtId]);

  useEffect(() => {
    const fetch = async (changeDistrictId: number) => {
      const request = await getSchoolsByDistrict(changeDistrictId);
      setIsFetchingSchools(false);
      if (request?.data?.schools && request.data.schools.length > 0) {
        setSchools(request.data.schools);
        console.log(schools);
        // setSchoolId(request.data.schools[0].schoolId ?? schoolId);
        // setModalSchoolId(request.data.schools[0].schoolId ?? schoolId);
      }
      else {
        setSchools([]);
      }
    }
    if (changeDistrictId > 0 && districtsList.find(x => x.districtId === changeDistrictId) && !isFetchingDistricts) {
      //We want to run this asynchronously so we build an async function
      //and then call it here
      setIsFetchingSchools(true);
      fetch(changeDistrictId);

      const currentDistrict = districtsList.find(x => x.districtId === districtId) ?? districtsList[0];
      setDistrict(currentDistrict);
      setIsDistrictEsy((currentDistrict?.esyStartDate && moment(currentDistrict?.esyStartDate).isValid() && moment(currentDistrict?.esyStartDate).isSameOrBefore(moment())) ?? false);
    }
    else {
      setSchools([]);
    }
  }, [changeDistrictId]);

  // Data
  const caseloadData = useAsync({
    promiseFn: fetchStudentDetails,
    id,
    onResolve: async (r) => {
      if (state?.idSpecific == 'caseload') {
        esyData.run(r.data.caseload.divisionId);
      }
      placementData.run(r.data.caseload.divisionId);

      setStudentFirstName(r.data.caseload?.studentFirstName ?? '')
      setStudentMi(r.data.caseload?.studentMi ?? '')
      setStudentSuffix(r.data.caseload?.studentSuffix ?? '')
      setStudentLastName(r.data.caseload?.studentLastName ?? '')
      setCaseloadId(r.data.caseload?.caseloadId ?? 0);
      setGender(r.data.caseload?.genderId ?? 0);
      setClientStudentId(r.data.caseload?.clientStudentId ?? '');
      setTreatment(r.data.caseload?.treatmentId ?? 0);
      setPlacement(r.data.caseload?.placementId ?? 0);

      setMaEligible(r.data.caseload?.maEligible ?? false);
      setEsy(r.data.caseload?.esyId ?? 0);
      setStudent(r.data.caseload?.student);
      setSchoolName(r.data.caseload?.schoolName ?? '');
      setDistrictName(r.data.caseload?.districtName ?? '');

      if (r.data?.caseload?.studentDOB) {
        setStudentDob(moment(r.data?.caseload.studentDOB).toDate());
      }

      if (r.data?.caseload?.gradeId) {
        setGrade(Number(r.data.caseload.gradeId));
      }

      if (r.data.caseload?.iepStartDate)
        setIepStartDate(moment(r.data.caseload.iepStartDate).toDate());

      if (r.data.caseload?.iepEndDate)
        setIepEndDate(moment(r.data.caseload.iepEndDate).toDate());

      if (r.data.caseload?.caseloadStartDate)
        setCaseloadStartDate(moment(r.data.caseload.caseloadStartDate).toDate());

      if (r.data.caseload?.caseloadEndDate)
        setCaseloadEndDate(moment(r.data.caseload.caseloadEndDate).toDate());

      setStudentId(r.data.caseload?.studentId ?? 0);
      setStudentStatus(r.data.caseload?.studentStatus);
      setDistrictId(r.data.caseload?.districtId ?? 0);
      setSchoolId(r.data.caseload?.schoolId ?? 0);
      setPlans(r.data.caseload?.treatmentPlans ?? []);
      setHomeDistrict(r.data?.caseload?.student?.homeDistrictId);
      startingHomeDistrict = r.data?.caseload?.student?.homeDistrictId ?? 0;

      setCaseload(r.data.caseload);

      setNotes(r.data.caseload?.notes ?? '');
      setIsRejected(r.data.caseload?.isRejected ?? false);
      setDefaultDistrict(r.data.caseload?.districtId ?? 0);
      homeDistrictsAsync.run(r.data?.caseload?.student?.homeDistrictId);

      //Get current user
      await fetchUser();

      //Get the disciplines for this caseload
      additionalCaseloadsData.run(r.data.caseload.studentId, r.data.caseload.caseloadId, r.data.caseload.disciplineId);
    },
  });

  /**
   * States
   */
  const statesData = useAsync({
    promiseFn: fetchStates,
    onResolve: (r) => {
      setStates(r.data?.states ?? []);
    }
  });

  /**
   * Home Districts
   */
  const homeDistrictsAsync = useAsync({
    deferFn: fetchHomeDistricts,
    onResolve: (r) => {
      if (r?.data) {
        const hd = !homeDistrict || homeDistrict === 0 ? startingHomeDistrict : startingHomeDistrict;
        const hdState = r.data?.find(x => x.homeDistrictId === hd)?.stateId ?? 0;
        setAllHomeDistricts(r.data ?? []);
        setHomeDistrictState(hdState);
        homeDistrictsByStateIdAsync.run(hdState);
      }
    }
  });

  const changeSchoolAsync = useAsync({
    deferFn: changeSchoolFunc,
    onResolve: (r) => {
      if (r?.data?.success) {
        setChangeSchoolModalOpen(false);
        setSchoolId(changeModalSchoolId);
        setDistrictId(changeDistrictId);
        setSchoolName(schools.find(x => x.schoolId === changeModalSchoolId)?.name ?? '')
        setDistrictName(districtsList.find(x => x.districtId === changeDistrictId)?.name ?? '')
      }
      else {
        setChangeErrorMessage(r.data?.message);
      }
    }
  })

  /**
   * Home District By States
   */
  const homeDistrictsByStateIdAsync = useAsync({
    deferFn: fetchHomeDistrictsByStateId,
    onResolve: (r) => {
      //Verify we have districts
      if (r?.data && r.data.length > 0) {
        //Generate our options list
        const options: option[] = [];
        r.data.map(x => options.push({ label: x.name ?? '', value: x.homeDistrictId }));

        //If no options are returned or the options are the same, do nothing
        if (options.length === 0 || options === homeDistricts) return;

        //Set the home districts for this state
        setHomeDistricts(options);

        //Get the current home district option
        const option = options.find(x => x.value === homeDistrict);

        //If the option is not set, return early
        if (!option) return;

        //Set our home district state
        const hdState = r.data.find(x => x.homeDistrictId === homeDistrict);
        if (hdState) {
          setHomeDistrictState(hdState?.stateId ?? 0);
        }

        //Set the home district option setting
        setHomeDistrictOption(options.find(x => x.value === homeDistrict));

        //Return early since we have our data
        return;
      }

      //If no data was returned, set the districts to an empty array
      setHomeDistricts([]);
      setHomeDistrict(0);
      setHomeDistrictOption(undefined);
    }
  });

  /**
   * Gets the school history
   */
  const schoolHistoryAsync = useAsync({
    deferFn: fetchSchoolHistory,
    onResolve: (r) => {
      setSchoolHistory(r.data);
      setShowSchoolHistoryModal(true);
    }
  });

  const additionalCaseloadsData = useAsync({
    deferFn: fetchCaseloadsByStudentId,
    onResolve: (data: AdditionalCaseloadsResponse) => {
      //Set the caseloads and sort them so the current therapist's caseloads show first
      const myCaseloads = [...data.data.additionalCaseloads.filter(x => x.therapist?.user?.userId === currentUser?.userId)];
      const otherCaseloads = [...data.data.additionalCaseloads.filter(x => x.therapist?.user?.userId !== currentUser?.userId).reverse()];
      const caseloads = [data.data.caseload, ...myCaseloads, ...otherCaseloads.reverse()];
      setCaseloads(caseloads);
    }
  });
  //Fetch data that doesn't need to be added to a hook like static data
  const gendersData = useAsync({ promiseFn: fetchGenders });
  const frequenciesData = useAsync({ promiseFn: fetchFrequencies });
  const gradesData = useAsync({ promiseFn: getGrades });
  const therapistRolesData = useAsync({ promiseFn: fetchTherapistRoles });

  const dischargeStudent = async () => {
    if (!caseloadId) {
      return Promise.reject('Missing caseloadId.');
    }

    try {
      // Ensure UTC midnight for discharge date
      const selectedDate = disciplineEndDate;
      const utcMidnight = selectedDate
        ? new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()))
        : undefined;

      return await dischargeFromDiscipline(caseloadId, { disciplineEndDate: utcMidnight });
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const getDischargeMessage = () => {
    var name = caseload?.disciplineName;
    return "This Action Will Discharge The Student From All " + name + " Services";
  };
  const discharge = useAsync({
    deferFn: dischargeStudent,
    onResolve: () => {
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      history.replace(`/caseload${(state.disciplineId && state.disciplineId !== 0 ? '?discipline=' + state.disciplineId : '')}`, {
        message: `The discharge request for ${caseloadData.data?.data.caseload.studentFirstName} ${caseloadData.data?.data.caseload.studentLastName} has been successfully processed!`,
        disciplineId: disciplineId,
      });
    },
  });

  const removeStudent = async () => {
    if (!caseloadId) {
      return Promise.reject('Missing caseloadId.');
    }
    try {
      return await removeStudentFromCaseload({
        disciplineId: disciplineId!,
        studentId: studentId,
        caseloadEndDate: caseloadEndDate!,
      });
    } catch (error: any) {
      return Promise.reject(error);
    }
  };
  const remove = useAsync({
    deferFn: removeStudent,
    onResolve: () => {

      history.replace(`/caseload${(state.disciplineId && state.disciplineId !== 0 ? '?discipline=' + state.disciplineId : '')}`, {
        message: `The remove request for ${caseloadData.data?.data.caseload.studentFirstName} ${caseloadData.data?.data.caseload.studentLastName} has been successfully processed!`,
        disciplineId: disciplineId,
      });
    },
  });

  const saveCaseload = async () => {
    if (!caseloadId) {
      return Promise.reject('Missing caseloadId.');
    }

    try {
      if ((isBillingSpecialist || isSuperUser || isAdmin) && caseloads.length > 0) {
        const request: CaseloadUpdateRequest = {
          //Content above tabs
          id: caseloadId,
          studentFirstName: studentFirstName,
          studentMi: studentMi,
          studentLastName: studentLastName,
          studentSuffix: studentSuffix,
          clientStudentId,
          studentId,
          placementId: placement === 0 ? undefined : placement,
          maEligible,
          genderId: gender,
          studentDob: studentDob?.toLocaleDateString(),
          grade,
          districtId,
          schoolId,
          treatmentId: treatment,
          homeDistrictId: homeDistrict === 0 ? null : homeDistrict,
          saveDetailsOnly: true,
          treatmentPlans: treatmentPlans
        }

        return updateCaseload(caseloadId, request);
      }

      //If we are not a billing specialist, we continue with the normal update process
      const requests: CaseloadUpdateRequest[] = [];

      //Loop only through the therapist's caseloads and add to the array
      caseloads.filter(c => c.therapist?.user?.userId === currentUser?.userId).map(c => {
        //Generate the treatment plans
        const treatmentPlans: TreatmentPlan[] = [];
        if (c.individual) {
          treatmentPlans.push({
            treatmentPlanTypeId: 1,
            treatmentFrequencyId: c.individualFrequency,
            treatmentPlanDuration: c.individualMinutes
          });
        }

        if (c.group) {
          treatmentPlans.push({
            treatmentPlanTypeId: 2,
            treatmentFrequencyId: c.groupFrequency,
            treatmentPlanDuration: c.groupMinutes
          });
        }

        if (c.consult) {
          treatmentPlans.push({
            treatmentPlanTypeId: 3,
            treatmentFrequencyId: c.consultFrequency,
            treatmentPlanDuration: c.consultMinutes
          });
        }

        const request: CaseloadUpdateRequest = {
          //Content above tabs
          id: c.caseloadId,
          clientStudentId,
          studentId,
          placementId: placement === 0 ? undefined : placement,
          maEligible,
          genderId: gender,
          studentDob: studentDob?.toLocaleDateString(),
          grade,
          districtId,
          schoolId,
          iepStartDate: iepStartDate?.toLocaleDateString(),
          iepEndDate: iepEndDate?.toLocaleDateString(),
          homeDistrictId: homeDistrict === 0 ? null : homeDistrict,
          treatmentPlans: treatmentPlans.length > 0 ? treatmentPlans : (c.treatmentPlans ?? []),

          //Tab content
          treatmentId: treatment,
          esyId: c.esyId,
          caseloadStartDate: new Date(c.caseloadStartDate)?.toLocaleDateString(),
          caseloadEndDate: c.caseloadEndDate?.toLocaleDateString(),
          studentStatusId: c.studentStatusId,
          therapistRoleId: c.therapistRoleId,
          studentStatus: c.studentStatus,
          individual: c.individual,
          consult: c.consult,
          group: c.group,
          individualFrequency: c.individualFrequency,
          consultFrequency: c.consultFrequency,
          groupFrequency: c.groupFrequency,
          individualMinutes: c.individualMinutes,
          consultMinutes: c.consultMinutes,
          groupMinutes: c.groupMinutes,
          on504: c.on504,
          onIEP: c.onIEP
        }

        //Loop through the entries and create a new promise for each approval
        requests.push(request);
      });

      //Perform the approvals
      await batchUpdateManyCaseloads(requests);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  /**
   * Saves the caseload(s)
   */
  const save = useAsync({
    deferFn: saveCaseload,
    onResolve: (r) => {
      //State parameters
      let studentDuplicate: boolean = r?.data?.studentDuplicate!;
      let stateParams: any = { message: `${caseloadData.data?.data.caseload.studentFirstName} ${caseloadData.data?.data.caseload.studentLastName} updated successfully!`, studentDuplicate: studentDuplicate };
      if (state && state !== null && state !== undefined) {
        if (state.disciplineId && state.disciplineId !== 0) stateParams.disciplineId = state.disciplineId;

        if (state.referrer === 'searchCaseloads') {
          history.push(`/caseload/searchCaseloads`, stateParams);
          return;
        }
      }

      history.push(`/caseload`, stateParams);
    },
  });

  

  const handleDischarge = () => discharge.run()
  const handleRemove = () => remove.run()

  const handleSubmit = () => save.run()

  const handleSubmitClick = () => {
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!caseloadEndDate || caseloadEndDate >= today || state?.idSpecific == 'student') {
      handleSubmit();
    } else {
      setOpenEndDateWarning(true);
    }
  };

  //Approval code
  const sendApprove = async () => {
    try {
      //First we save the caseload...
      await saveCaseload();

      //... and then we approve it
      await approveCaseload(caseloadId ?? 0);

      history.push('/caseload/approve', { message: `Caseload approved successfully!` })
    } catch (error: any) {
      return Promise.reject(error);
    }
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const doApprove = useAsync({ deferFn: sendApprove });

  //Rejection code
  const sendReject = async () => {
    try {
      const request: CaseloadApprovalRequest = {
        caseloadId,
        notes: newNotes
      }
      await rejectCaseload(caseloadId!, request);

      history.push('/messages', { message: `Caseload rejected successfully!` });
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const doReject = useAsync({ deferFn: sendReject });

  /**
   * When the home district state changes, get the home districts for that state
   */
  useEffect(() => {
    //If not a valid state ID or if we are already fetching, exit early
    if (Number.isNaN(homeDistrictState) || homeDistrictsByStateIdAsync.isPending) return;
    homeDistrictsByStateIdAsync.run(homeDistrictState);
  }, [homeDistrictState]);

  /**
   * Handles the change event for the home district.
   *
   * @param {SingleValue<option>} e - The selected option for the home district.
   */
  const handleHomeDistrictChange = (e: SingleValue<option>) => {
    if (e) {
      setHomeDistrict(Number(e.value));
      setHomeDistrictOption(e ?? { label: '', value: '' });
    }
  };
  /**
   * Handles the change event on the home district state select element.
   *
   * @param {React.FormEvent<HTMLSelectElement>} e - The change event object.
   * @return {void} Nothing is returned from this function.
   */
  const handleHomeDistrictStateChange = (e: { value: number; label: string } | null) => {
    //If this is the state that is already selected, exit early
    if (Number(e?.value) === homeDistrictState) return;

    //Otherwise, reset everything and change the state (which will fire the effect above)
    setHomeDistrictOption(undefined);
    setHomeDistrict(0);
    setHomeDistrictState(Number(e?.value));
  }

  // Error handling
  if (
    gendersData.error ||
    caseloadData.error ||
    placementData.error ||
    esyData.error
  )
    return <LayoutError />;

  return (
    <Fragment>
      <EditModal
        open={openRejectConfirmModal}
        saveText='Confirm'
        onSave={() => { doReject.run(); }}
        onCancel={() => { setOpenRejectConfirmModal(false) }}
        id={caseloadId}
      >
        <h3>Are you sure you would like to reject this caseload?</h3>
        <textarea value={newNotes ?? ''} onChange={(e) => setNewNotes(e.target.value)}></textarea>
      </EditModal>
      <Modal
        saveText='Proceed'
        open={openEndDateWarning}
        onCancel={() => setOpenEndDateWarning(false)}
        onSave={() => {
          setOpenEndDateWarning(false);
          handleSubmit();
        }}
      >
        <Fragment>
          <div className='grid grid-cols-1'>
            <p>
              The student will be removed from your caseload immediately based
              on the Caseload End Date you entered. Do you want to proceed?
            </p>
          </div>
        </Fragment>
      </Modal>

      <Modal
        saveText='Proceed'
        allowSave={
          disciplineEndDate !== undefined && disciplineEndDate !== null
        }
        open={openDischargeWarning}
        onCancel={() => {
          setOpenDischargeWarning(false);
          setDisciplineEndDate(undefined);
        }}
        onSave={() => {
          setOpenDischargeWarning(false);
          handleDischarge();
        }}
      >
        <Fragment>
          <div className='grid grid-cols-1 mb-16'>
            <label className='mb-2 text-lg'>Discharge Student</label>
            <p>
              Are you sure you want to discharge this student? This action will
              remove the student from all{' '}
              {caseload?.disciplineName ?? ''} services for all
              therapists.
            </p>
            <div className='pb-16 mt-3 mb-16'>
              <label className='primary'>Discharge Date</label>
              <DatePicker
                className='block mt-1 primary'
                selected={disciplineEndDate}
                onChange={(date: Date) => setDisciplineEndDate(date!)}
                minDate={moment(caseload?.disciplineStartDate).toDate()}
              />
            </div>
          </div>
        </Fragment>
      </Modal>

      <Modal
        saveText='Proceed'
        allowSave={
          caseloadEndDate !== undefined && caseloadEndDate !== null
        }
        open={openRemoveWarning}
        onCancel={() => {
          setOpenRemoveWarning(false);
          setCaseloadEndDate(undefined);
        }}
        onSave={() => {
          setOpenRemoveWarning(false);
          handleRemove();
        }}
      >
        <Fragment>
          <div className='grid grid-cols-1 mb-16'>
            <label className='mb-2 text-lg'>Remove From Caseload</label>
            <p>
              Are you sure you want to remove from caseload? The student will come off
              the caseload but will remain in the system as eligible for services.
            </p>
            <div className='pb-16 mt-3 mb-16'>
              <label className='primary'>Date to End Caseload</label>
              <DatePicker
                className='block mt-1 primary'
                selected={caseloadEndDate}
                onChange={(date: Date) => setCaseloadEndDate(date!)}
                minDate={moment(caseload?.startDate).toDate()}
              />
            </div>
          </div>
        </Fragment>
      </Modal>

      <Modal
        saveText='Change School'
        open={changeSchoolModalOpen}
        onCancel={() => {
          setChangeSchoolModalOpen(false);
          setChangeErrorMessage('');
        }}
        onSave={() => { changeSchoolAsync.run(studentId, changeModalSchoolId, modalSchoolStartDate?.toLocaleDateString()) }}
        allowSave={schoolId != changeModalSchoolId && modalSchoolStartDate != null && changeDistrictId != 0 && changeModalSchoolId != 0}
      >
        <Fragment>
          <div className='grid grid-cols-1 mb-16' style={{ minHeight: '400px' }}>
            <label className='mb-2 text-lg'>Change school</label>
            <br />

            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
              <label className='primary'><span style={{ color: 'red' }}>*</span>District</label>
              <Select
                onChange={(e) => setChangeDistrictId(Number(e?.value))}
                className='block primary'
                value={changeDistrictId ? { value: changeDistrictId, label: districtsList?.find((d) => d.districtId === changeDistrictId)?.name ?? '' } : null}
                options={districtsList && districtsList.length > 0 ? districtsList.sort((a, b) => a.name.localeCompare(b.name)).map((t: District) => ({ value: t.districtId, label: t.name })) : []}
                placeholder=' -- Choose District --'
                styles={graySelectStyles}
                isSearchable={false}
              />
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
              <label className='primary'><span style={{ color: 'red' }}>*</span>School</label>
              <Select
                onChange={(e) => setChangeModalSchoolId(Number(e?.value))}
                className='block primary'
                value={changeModalSchoolId ? { value: changeModalSchoolId, label: schools.find((s) => s.schoolId === changeModalSchoolId)?.name ?? '' } : null}
                options={schools.length > 0 ? schools.sort((a, b) => a.name.localeCompare(b.name)).filter(x => moment(districtsList.find(x => x.districtId === districtId)?.esyStartDate).isValid() ? true : !x.internal).map((t: School) => ({ value: t.schoolId, label: t.name })) : []}
                placeholder={schools.length > 0 ? ' -- Choose School --' : ' -- No Schools --'}
                styles={graySelectStyles}
                isSearchable={false}
              />
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-2 mt-2'>
              <label className='primary'><span style={{ color: 'red' }}>*</span> Start date</label>
              <DatePicker
                onChange={(date: Date) => setModalSchoolStartDate(date)}
                selected={modalSchoolStartDate}
              />
            </div>
            {ChangeErrorMessage ? (<p className='mt-5' style={{ color: 'red' }}>{ChangeErrorMessage}</p>) :
              (<p className='mt-5'>
                The start date set will be when the student will be available at the new school.
                If the student is in another school, that schools end date will be set to 1 day prior to the new schools start date
              </p>)
            }
          </div>
        </Fragment>
      </Modal>
      <StudentSchoolHistoryModal show={showSchoolHistoryModal} setShow={setShowSchoolHistoryModal} studentSchoolHistory={schoolHistory} />

      <Layout
        isLoading={
          gendersData.isPending ||
          caseloadData.isPending ||
          placementData.isPending ||
          esyData.isPending ||
          doApprove.isPending ||
          doReject.isPending ||
          gradesData.isPending ||
          therapistRolesData.isPending ||
          additionalCaseloadsData.isPending ||
          frequenciesData.isPending ||
          schoolHistoryAsync.isPending ||
          isFetchingSchools ||
          statesData.isPending ||
          //homeDistrictsData.isPending ||
          homeDistrictsByStateIdAsync.isPending ||
          homeDistrictsAsync.isPending ||
          changeSchoolAsync.isPending
        }
      >
        <Fragment>
          {save.error && <ResponseStatus state={save} />}
          {discharge.error && <ResponseStatus state={discharge} />}
          {state && state !== undefined && state !== null && (
            <>
              {hasValue(state.therapistFirstName) && hasValue(state.therapistLastName) ?
                <Notification isSuccess={true} isOpen={true} message={`${state.therapistFirstName} ${state.therapistLastName} has requested the following student to be added to their caseload`} />
                : <></>}
            </>
          )}
          {isRejected && notes && notes !== '' && <Notification isSuccess={false} isOpen={true} message={`Rejected: ${notes}`} />}
          <div className='grid grid-cols-6 gap-6 mb-6'>
            {/*<button onClick={generateJsonFromTabs}>Get JSON</button>*/}
            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              {(isSuperUser || isBillingSpecialist) && <FieldInput
                name='studentFirstName'
                label='First Name'
                onChange={(e) => setStudentFirstName(e.target.value)}
                value={studentFirstName}
              />}
              {(!isSuperUser && !isBillingSpecialist) && (
                <div>
                  <label className='primary'>First Name</label>
                  <p>{caseload?.studentFirstName} </p>
                </div>
              )}
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              {(isSuperUser || isBillingSpecialist) && <FieldInput
                name='studentMi'
                label='MI'
                onChange={(e) => setStudentMi(e.target.value)}
                value={studentMi}
              />}
              {(!isSuperUser && !isBillingSpecialist) && (
                <div>
                  <label className='primary'>MI</label>
                  <p>{caseload?.studentMi} </p>
                </div>
              )}
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              {(isSuperUser || isBillingSpecialist) && <FieldInput
                name='studentLastName'
                label='Last Name'
                onChange={(e) => setStudentLastName(e.target.value)}
                value={studentLastName}
              />}
              {(!isSuperUser && !isBillingSpecialist) && (
                <div>
                  <label className='primary'>Last Name</label>
                  <p>{caseload?.studentLastName} </p>
                </div>
              )}
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-3'>
              {(isSuperUser || isBillingSpecialist) && <FieldInput
                name='studentSuffix'
                label='Suffix'
                onChange={(e) => setStudentSuffix(e.target.value)}
                value={studentSuffix}
              />}
              {(!isSuperUser && !isBillingSpecialist) && (
                <div>
                  <label className='primary'>Suffix</label>
                  <p>{caseload?.studentSuffix} </p>
                </div>
              )}

            </div>
            <div className='hidden col-span-6 lg:col-span-3'></div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              <label className='primary'>Date Of Birth</label>
              {!isAdmin &&
                <DatePicker
                  onChange={(date: Date) => setStudentDob(date)}
                  maxDate={new Date()}
                  selected={studentDob && studentDob !== undefined && studentDob !== null ? studentDob : new Date()}
                />
              }

              {isAdmin &&
                <p>
                  {moment(caseload?.studentDOB).format(
                    'MM/DD/YYYY'
                  )}
                </p>
              }
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              <label className='primary'>Grade</label>
              {/* If a therapist, display a dropdown for the grade */}
              {(isTherapist || isBillingSpecialist || isSuperUser) &&
                <Select
                  onChange={(e) => setGrade(Number(e?.value))}
                  className='block primary'
                  value={grade ? { value: grade, label: gradesData?.data?.data.grades.find((g) => g.gradeId === grade)?.name ?? '' } : null}
                  options={gradesData?.data?.data.grades.sort((a, b) => parseInt(a.name) - parseInt(b.name)).map((g) => ({ value: g.gradeId, label: g.name })) ?? []}
                  placeholder=' -- Grade --'
                  styles={graySelectStyles}
                  isSearchable={false}
                />
              }

              {/* If not a therapist, simply show the grade */}
              {!(isTherapist || isBillingSpecialist || isSuperUser) &&
                caseloadData?.data?.data?.caseload?.gradeName &&
                <p>{caseloadData?.data?.data?.caseload?.gradeName} </p>
              }
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              <label className='primary'>Gender</label>
              <Select
                onChange={(e) => setGender(Number(e?.value))}
                className='block primary'
                value={gender ? { value: gender, label: gendersData?.data?.data?.genders?.find((g) => g.genderId === gender)?.name ?? '' } : null}
                isDisabled={!isTherapist && !isBillingSpecialist && !isSuperUser}
                options={gendersData?.data?.data?.genders ? gendersData.data?.data.genders.sort((a, b) => a.name.localeCompare(b.name)).map((g) => ({ value: g.genderId, label: g.name })) : []}
                placeholder=' -- Choose Gender --'
                styles={graySelectStyles}
                isSearchable={false}
              />
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-3'>
              <FieldInput
                name='clientStudentId'
                label='Client Student ID'
                onChange={(e) => setClientStudentId(e.target.value)}
                value={clientStudentId}
                disabled={!isTherapist && !isBillingSpecialist && !isSuperUser}
              />
            </div>

            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
              <FieldInput
                name='district'
                label='District'
                value={districtName}
                disabled={true}
                readonly={true}
              /> </div>

            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              <label className='primary'>
                Home District State
              </label>
              <Select
                onChange={(e) => handleHomeDistrictStateChange(e)}
                className='block mt-1 primary'
                value={homeDistrictState ? { value: homeDistrictState, label: states?.find((s) => s.stateId === homeDistrictState)?.name ?? '' } : null}
                options={states ? states.map((s) => ({ value: s.stateId, label: s.name })) : []}
                placeholder=' -- Choose State --'
                styles={graySelectStyles}
                isSearchable={false}
              />
            </div>

            <div className='col-span-6 sm:col-span-6 lg:col-span-3'>
              <label className='primary'>
                Home District
              </label>
              <Select
                onChange={(e) => handleHomeDistrictChange(e)}
                className='block mt-1 primary'
                value={homeDistrictOption ?? { label: '', value: 0 } as option}
                options={homeDistricts ?? []}
                placeholder={`Select Home District...`}
                isSearchable
              />
            </div>

            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
              <FieldInput
                name='school'
                label='School'
                value={schoolName}
                disabled={true}
                readonly={true}
              />
              <span
                className='block w-2/5 pl-1 text-ptsblue-normal underline cursor-pointer hover:no-underline'
                onClick={() => schoolHistoryAsync.run(studentId)}>View School History</span>
            </div>

            <div className='col-span-6 sm:col-span-6 lg:col-span-1'>
              <button className='mt-5'
                onClick={() => {
                  setChangeSchoolModalOpen(true);
                  setChangeDistrictId(0);
                  setChangeModalSchoolId(0);
                  setModalSchoolStartDate(undefined);
                  setSchools([]);
                }
                }
              >
                Change school
              </button>
            </div>

            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
              <label className='primary'>MA Eligible</label>
              <div>
                <input
                  id='is-visible'
                  name='is-visible'
                  type='checkbox'
                  checked={maEligible}
                  disabled={!isTherapist && !isBillingSpecialist && !isSuperUser}
                  onChange={(e) => setMaEligible(e.currentTarget.checked)}
                />
              </div>
            </div>
            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
              <label className='primary'>Placement <span className='pl-1 text-red-500'>*</span></label>
              <Select
                onChange={(e) => setPlacement(Number(e?.value))}
                className='block mt-1 primary'
                value={placement ? { value: placement, label: placementData?.data?.data?.placements?.find((g) => g.placementId === placement)?.name ?? '' } : null}
                isDisabled={!isTherapist && !isBillingSpecialist && !isSuperUser}
                options={placementData?.data?.data?.placements ? placementData?.data?.data?.placements.sort((a, b) => a.name.localeCompare(b.name)).map((g) => ({ value: g.placementId, label: g.name })) : []}
                placeholder=' -- Select --'
                styles={graySelectStyles}
                isSearchable={false}
              />
            </div>
          </div>

          <div className="tab-content">
            {/* Caseload Tabs */}
            {/* If we have student disciplines, display the tab group */}
            {(!(frequenciesData.isFulfilled &&
              studentStatusesData.isFulfilled &&
              therapistRolesData.isFulfilled &&
              additionalCaseloadsData.isFulfilled &&
              esyData.isFulfilled/* &&
              studentDisciplines.length > 0*/ ) && state?.idSpecific == 'caseload') &&
              <h2>Loading...</h2>
            }

            {frequenciesData.isFulfilled &&
              studentStatusesData.isFulfilled &&
              therapistRolesData.isFulfilled &&
              additionalCaseloadsData.isFulfilled &&
              esyData.isFulfilled &&
              <>
                {/* Tabs */}
                <Tab.Group onChange={setSelectedTab}>
                  {/* Tab List */}
                  <Tab.List className='tabs'>
                    {/*Verify we have caseload data and loop through
                    the student disciplines to create the tabs*/}
                    {caseloads
                      //?.filter(x => x.therapist?.user?.userId === caseloads[0]?.therapist?.user?.userId)
                      //.filter(x => x.endDate !== null || moment(x.endDate).isAfter(moment()))
                      .map((c) => <Tab as={Fragment} key={`sd-tab-${c?.caseloadId}-${c.disciplineId}`}>
                        {(btn) => (
                          <button className={btn.selected ? 'active' : ''}>
                            {c?.disciplineName}
                          </button>
                        )}
                      </Tab>
                      )}
                  </Tab.List>

                  {/* Tab Content */}
                  <Tab.Panels className='tabpanels'>
                    {caseloads
                      //?.filter(x => x.therapist?.user?.userId === caseloads[0]?.therapist?.user?.userId)
                      .map((c, i) => <EditStudentTabPanel
                        key={`sd-tab-panel-${c?.caseloadId}-${c.disciplineId}`}
                        caseload={c}
                        currentUser={currentUser}
                        currentTab={selectedTab}
                        sendToParent={setTabContent}
                        setTrigger={setTabContentTrigger}
                        iepStartDate={iepStartDate}
                        iepEndDate={iepEndDate}
                        setIepStartDate={setIepStartDate}
                        setIepEndDate={setIepEndDate}
                        treatmentsAssigned={treatmentsAssigned}
                        setTreatmentsAssigned={setTreatmentsAssigned}
                        studentStatus={studentStatus ?? ''}
                        setStudentStatus={setStudentStatus}
                        trigger={tabContentTrigger}
                        treatmentPlans={plans}
                        setTreatmentOption={setTreatment}
                        data={{
                          frequencies: frequenciesData.data?.data.frequencies,
                          caseloads,
                          therapistRoles: therapistRolesData.data?.data.therapistRoles,
                          studentStatuses: studentStatusesData.data?.data?.studentStatuses,
                          esy: esyData.data?.data.esys,
                          treatmentPlans: plans
                        }}
                      />
                      )}
                  </Tab.Panels>
                </Tab.Group>
              </>
            }
          </div>
          {/* End Tab.Group Conditional*/}

          {/* Bottom Button Bar - Submit, Back, etc. */}
          <div className='flex justify-between'>
            <SubmitButton
              className='mt-2 mb-1 secondary'
              text='Back'
              onClick={() => {
                if (state !== undefined && state.referrer && state.referrer !== null && state.referrer !== undefined) {
                  switch (state.referrer) {
                    case 'approve':
                      history.goBack();
                      return;
                    default:
                      history.push(`/caseload/${state.referrer}`);
                      return;
                  }
                }

                history.push(`/caseload${(state.disciplineId && state.disciplineId !== 0 ? '?discipline=' + state.disciplineId : '')}`, { disciplineId: disciplineId, });
              }}
            />
            <div className=''>
              {(IsAllowed([Role.Therapist], false, userRoles)) && (
                <SubmitButton
                  text='Remove from Caseload'
                  spinning={remove.isPending}
                  onClick={() => setOpenRemoveWarning(true)}
                  className='ml-1'
                />)}
              {state !== undefined && state && state.referrer && state.referrer !== null && state.referrer !== undefined ? (
                <>
                  {state !== undefined && state.referrer === 'approve' &&
                    <SubmitButton
                      text='Approve'
                      onClick={() => { doApprove.run(); }}
                      className='mt-2 ml-2'
                    />
                  }
                </>
              ) :
                !caseload?.disciplineEndDate && (
                  <span data-tip={getDischargeMessage()} data-for='toolTip1'>
                    <SubmitButton
                      text='Discharge from Discipline'
                      spinning={discharge.isPending}
                      onClick={() => setOpenDischargeWarning(true)}
                      className='ml-1'
                      data-tip="msg to show" data-for='toolTip1' data-place='top'
                    />
                    <ReactToolTip id='toolTip1' /></span>
                )}
              {state !== undefined && state.referrer && state.referrer !== null && state.referrer !== undefined && state.referrer !== 'searchCaseloads' ? (
                <>
                  {state !== undefined && state.referrer === 'approve' &&
                    <SubmitButton
                      text='Reject'
                      onClick={() => { setOpenRejectConfirmModal(true) }}
                      className='ml-1 bg-red-500 hover:bg-red-500'
                    />
                  }
                </>
              ) : <SubmitButton
                text='Update'
                spinning={save.isPending}
                onClick={handleSubmitClick}
                className='mt-2 ml-2'
                disabled={
                  //Filter all caseloads to only the ones owned by this therapist
                  caseloads
                    .filter(x => x.therapist?.user?.userId === currentUser?.userId)
                    .some(x => !x.studentStatusId || x.studentStatusId < 1 ||
                      !x.caseloadStartDate || !moment(x.caseloadStartDate).isValid() ||
                      !x.therapistRoleId || x.therapistRoleId < 1 ||
                      !x.esyId || x.esyId < 1 ||
                      (x.treatmentDatesRequired && (
                        !x.iepStartDate || !moment(x.iepStartDate).isValid() ||
                        !x.iepEndDate || !moment(x.iepEndDate).isValid()
                      ))
                    ) ||
                  //Filter all caseloads to this therapist and the ones that only have 'On Caseload' set
                  caseloads
                    .filter(x =>
                      x.therapist?.user?.userId === currentUser?.userId &&
                      x.studentStatus === 'On Caseload' &&
                      (x.on504 === true || x.onIEP === true))
                    .some(x => !x.iepStartDate || !moment(x.iepStartDate).isValid()) ||
                  //Filter all caseloads to this therapist and the ones that only have 'On Caseload' set and is IEP
                  caseloads
                    .filter(x =>
                      x.therapist?.user?.userId === currentUser?.userId &&
                      x.studentStatus === 'On Caseload' &&
                      x.onIEP)
                    .some(x =>
                      (!x.group && !x.individual && !x.consult) ||
                      (x.group && (x.groupFrequency === 0 || x.groupMinutes === 0)) ||
                      (x.individual && (x.individualFrequency === 0 || x.individualMinutes === 0)) ||
                      (x.consult && (x.consultFrequency === 0 || x.consultMinutes === 0))
                    ) ||
                  (placement == undefined || placement == 0)
                }
              />}
            </div>
          </div>
        </Fragment>
      </Layout>
    </Fragment>
  );
};

export default EditStudent;
